import { Controller } from "@hotwired/stimulus"

interface HashcashOptions {
  resource: string
  bits: number
  date: string
}

declare global {
  interface Window {
    Hashcash?: {
      mint(
        resource: string,
        options: HashcashOptions,
        done: (stamp: { toString(): string }) => void,
      ): void
    }
  }
}

interface Mint {
  input: HTMLInputElement
  options: HashcashOptions
  done: () => void
}

// The gem runs one worker at a time and starting a second kills the first,
// so mints queue: an item page can show two join forms at once, and the
// second would otherwise never get its stamp.
const pending: Mint[] = []
let running: Mint | null = null

function pump() {
  if (running || !window.Hashcash) return

  const next = pending.shift()
  if (!next) return
  if (!next.input.isConnected) return pump()

  running = next
  window.Hashcash.mint(next.options.resource, next.options, (stamp) => {
    running = null
    if (next.input.isConnected) {
      next.input.value = stamp.toString()
      next.done()
    }
    pump()
  })
}

function forget(input: HTMLInputElement) {
  for (let i = pending.length - 1; i >= 0; i--) {
    if (pending[i].input === input) pending.splice(i, 1)
  }
  if (running?.input === input) {
    running = null
    pump()
  }
}

// A stamp proves this browser did a little work before an email goes out to a
// typed address (#2283). Minting starts the moment the form appears, so it is
// usually done before the address is typed. The gem's own form driver is not
// used: it rewrites the submit button's markup, which the magic-link
// controller owns.
export default class extends Controller<HTMLFormElement> {
  static targets = ["stamp", "submit"]

  declare readonly hasStampTarget: boolean
  declare readonly stampTarget: HTMLInputElement
  declare readonly submitTargets: HTMLButtonElement[]

  connect() {
    this.mint({ holdSubmit: true })
  }

  // Leaving the page terminates the worker without a word, so a mint that
  // was running must not block the queue for the next page.
  disconnect() {
    if (this.hasStampTarget) forget(this.stampTarget)
  }

  // A stamp can be spent once, so a form that stays on the page after an
  // attempt takes a fresh one. The buttons are left alone: the magic-link
  // controller is showing the outcome on them.
  remint() {
    this.mint({ holdSubmit: false })
  }

  // A password manager can submit before the stamp exists; the server would
  // only refuse it.
  guard(event: Event) {
    if (!this.hasStampTarget || this.stampTarget.value) return

    event.preventDefault()
    event.stopImmediatePropagation()
  }

  private mint({ holdSubmit }: { holdSubmit: boolean }) {
    if (!this.hasStampTarget) return

    const input = this.stampTarget
    const options = JSON.parse(
      input.dataset.hashcash ?? "{}",
    ) as HashcashOptions
    forget(input)
    input.value = ""
    if (holdSubmit)
      this.submitTargets.forEach((button) => (button.disabled = true))

    pending.push({
      input,
      options,
      done: () => {
        if (holdSubmit)
          this.submitTargets.forEach((button) => (button.disabled = false))
      },
    })
    pump()
  }
}
